import { useState, type DragEvent } from 'react';
import { Loader2Icon, UploadCloudIcon } from 'lucide-react';
import { ACCEPTED_DOCUMENT_EXTENSIONS, ACCEPTED_DOCUMENT_MIME_TYPES } from '@shared/schemas/document';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { useDocumentDeposits } from '../hooks/useDocumentDeposits';
import { partitionDepositFiles } from '../lib/deposit-name-conflict';
import { collectFilesFromDataTransfer } from '../lib/folder-traversal';
import { formatSize } from '../lib/document-format';
import { DocumentFilePicto } from './DocumentFilePicto';

const ACCEPT_ATTR = [...ACCEPTED_DOCUMENT_MIME_TYPES, ...ACCEPTED_DOCUMENT_EXTENSIONS].join(',');

function replaceDialogCopy(manualCount: number, sharepointCount: number) {
  if (manualCount === 0) {
    return {
      title: 'Ce nom est déjà utilisé',
      description: sharepointCount > 1
        ? 'Ces documents viennent de SharePoint. Ils ne peuvent pas être remplacés depuis ici.'
        : 'Ce document vient de SharePoint. Il ne peut pas être remplacé depuis ici.',
    };
  }

  if (sharepointCount > 0) {
    return {
      title: 'Certains fichiers existent déjà',
      description: 'Les dépôts manuels remplacent l’ancienne version. Les documents SharePoint restent inchangés.',
    };
  }

  return {
    title: manualCount > 1 ? 'Remplacer les fichiers existants ?' : 'Remplacer le fichier existant ?',
    description: manualCount > 1
      ? 'Ces noms sont déjà dans la base. Les remplacer retire les anciennes versions de la recherche.'
      : 'Un document du même nom est déjà dans la base. Le remplacer retire l’ancienne version de la recherche.',
  };
}

export function DocumentDeposits() {
  const [isDragging, setIsDragging] = useState(false);
  const { isBatchUploading, processFiles, replacePrompt, onReplacementOpenChange, chooseReplacement, uploadQueue } = useDocumentDeposits();
  async function onDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setIsDragging(false);
    const files = await collectFilesFromDataTransfer(event.dataTransfer);
    await processFiles(files);
  }

  const replacePartition = replacePrompt
    ? partitionDepositFiles(replacePrompt.files, replacePrompt.conflicts)
    : null;
  const sharepointDeposits = new Set(replacePartition?.sharepoint ?? []);
  const replaceCopy = replacePartition
    ? replaceDialogCopy(replacePartition.manual.length, replacePartition.sharepoint.length)
    : null;
  const uploadNoticesLeaving = uploadQueue.length > 0 && uploadQueue.every((item) => item.dismissing);

  return (
    <>
      <label
        className={[
          'flex min-h-36 cursor-pointer items-center justify-center rounded-[var(--radius-surface-3xl)] border-2 border-dashed bg-card px-6 py-8 text-center transition',
          isDragging ? 'border-foreground/30 bg-muted/40' : 'border-border/70',
        ].join(' ')}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
      >
        <input
          accept={ACCEPT_ATTR}
          className="hidden"
          data-testid="document-upload-input"
          multiple
          type="file"
          onChange={(event) => {
            void processFiles(Array.from(event.target.files ?? []));
            event.currentTarget.value = '';
          }}
        />
        <div className="flex flex-col items-center gap-3">
          <div
            className={cn(
              'flex size-20 items-center justify-center rounded-3xl border border-border/60 bg-muted/40 text-foreground shadow-sm transition',
              isDragging && 'scale-105 border-foreground/30 bg-muted/70',
            )}
            aria-hidden
          >
            {isBatchUploading ? (
              <Loader2Icon className="size-11 animate-spin text-foreground" />
            ) : (
              <UploadCloudIcon className="size-11 text-foreground/80" strokeWidth={1.5} />
            )}
          </div>
          <p className="text-sm font-medium text-foreground">
            Glissez vos fichiers ou un dossier ici, ou cliquez pour sélectionner
          </p>
          <p className="max-w-prose text-xs text-muted-foreground">
            Formats acceptés : PDF, DOCX, XLSX, XLS, ODS, CSV, TSV, DWG (textes, calques, blocs, cotes ; en-têtes R13 à R2018 ; extraction parfois partielle ; plan non affiché).
            Déposer un fichier dont le nom existe déjà ouvre une demande : remplacer ou annuler.
            Les dossiers déposés sont parcourus récursivement (sous-dossiers inclus) ; les fichiers non supportés sont ignorés.
          </p>
        </div>
      </label>
      {uploadQueue.length > 0 ? (
        <div
          className={cn(
            'my-0! grid transition-[grid-template-rows] duration-500 ease-out motion-reduce:transition-none',
            uploadNoticesLeaving ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]',
          )}
        >
          <div className="min-h-0 overflow-hidden">
          <ul className="pb-6 text-xs" data-testid="document-upload-results">
            {uploadQueue.map((item) => {
              const othersRemain = uploadQueue.some((entry) => entry.id !== item.id && !entry.dismissing);
              return (
                <li
                  key={item.id}
                  className={cn(
                    'grid transition-[grid-template-rows,opacity] duration-500 ease-out motion-reduce:transition-none',
                    item.dismissing && othersRemain ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr]',
                    item.dismissing ? 'opacity-0' : 'opacity-100',
                  )}
                >
                  <div className={cn('min-h-0 overflow-hidden', item.status === 'failed' ? 'text-destructive' : 'text-muted-foreground')}>
                    <p className="py-0.5">
                      <span className="font-medium text-foreground">{item.name}</span>
                      {' — '}
                      {item.status === 'cancelled'
                        ? (item.errorMessage ?? 'Ajout annulé.')
                        : item.status === 'failed'
                        ? (item.errorMessage ?? 'Le dépôt a échoué.')
                        : item.status === 'uploaded'
                          ? (item.replacedPrevious
                            ? 'Remplace la version précédente. Indexation en cours.'
                            : 'Déposé. Indexation en cours.')
                          : item.status === 'uploading'
                            ? 'Envoi…'
                            : 'En attente'}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
          </div>
        </div>
      ) : null}

      <AlertDialog
        open={replacePrompt !== null}
        onOpenChange={onReplacementOpenChange}
      >
        <AlertDialogContent data-testid="document-replace-dialog" className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {replaceCopy?.title ?? 'Remplacer le fichier existant ?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {replaceCopy?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {replacePrompt ? (
            <ul
              aria-label="Fichiers concernés"
              className="max-h-52 space-y-2 overflow-y-auto"
            >
              {replacePrompt.files.map((file) => {
                const sharepoint = sharepointDeposits.has(file);
                return (
                  <li
                    key={`${file.name}-${file.size}-${file.lastModified}`}
                    className="flex items-center gap-3 rounded-2xl bg-muted/50 px-3 py-2.5"
                  >
                    <DocumentFilePicto mimeType={file.type} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatSize(file.size)}
                        {' · '}
                        {sharepoint ? 'SharePoint, conservé' : 'remplace la version actuelle'}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => chooseReplacement('cancel')}>
              Annuler l’ajout
            </AlertDialogCancel>
            {replacePartition && replacePartition.manual.length > 0 ? (
              <AlertDialogAction onClick={() => chooseReplacement('replace')}>
                Remplacer
              </AlertDialogAction>
            ) : null}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </>
  );
}
